import { isEqual } from "lodash";
import { v4 as uuid } from 'uuid';
import { distinctUntilChanged, take } from "rxjs/operators";
import { Editor, type RawEditorOptions } from 'tinymce';
import { Component, Input, NgZone, type OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Output, EventEmitter, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateService } from '@ngx-translate/core';
import { AbstractControl, ControlContainer, FormGroupDirective } from '@angular/forms';
import { DialogService } from 'primeng/dynamicdialog';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatFacades from '@medsurf/flat-facades';
import { MediaControlService } from '@medsurf/flat-services';
import * as EditorPlugins from './plugin/internal';
import { getEditorConfig } from "./config";

@Component({
    selector: 'medsurf-editor',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './editor.component.html',
    styleUrls: ['./editor.component.scss'],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective
        }
    ],
    standalone: false
})
export class EditorComponent implements OnInit {
  @Input() formField = true;
  @Input() control?: AbstractControl;
  @Input() toolbar?: string[][];
  @Input() toolbarExcludes?: string[];
  @Input() placeholder?: string;
  @Input() label?: string;
  @Input() minHeight = 200;
  @Input() fullHeight = false;
  @Input() autogrow = true;
  @Input() inPlace = true;
  @Output() valueChange = new EventEmitter<void>();
  
  active = signal(false);

  #editor: Editor;
  #previousValue: string | null = null;
  init: Record<string, any>;
  name!: string | null;
  id = uuid();
  readonly FlatModels = FlatModels;
  
  constructor(protected slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              private translate: TranslateService,
              private dialogService: DialogService,
              private mediaControlService: MediaControlService,
              private cd: ChangeDetectorRef,
              private _ngZone: NgZone,
              private destroyRef: DestroyRef) {
  }

  ngOnInit() {
    const formGroup = this.control?.parent;
    this.name = formGroup ? (Object.keys(formGroup.controls).find(name => this.control === formGroup.get(name)) || null) : null;
	  
	  if (this.name === null) {
		  throw new FlatModels.ErrorControlModels.MedsurfError(
			  'notification.form_control_name_not_found',
			  FlatModels.ErrorControlModels.ErrorLevel.enum.error,
			  FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
			  "APPS.Author.EditorComponent.ngOnInit"
		  );
	  }

    this.control?.statusChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged()).subscribe(() => {
      this.cd.markForCheck();
    });

    this.control?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged((a, b) => isEqual(a, b))).subscribe(() => {
      this.cd.detectChanges();
    });
    
    this.control?.parent?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged((a, b) => isEqual(a, b))).subscribe(() => {
      this.cd.detectChanges();
    });
  }

  get initalSettings(): RawEditorOptions {
    const editorConfig = getEditorConfig({
      placeholder: this.placeholder,
      toolbar: this.toolbar,
      toolbarExcludes: this.toolbarExcludes,
      autogrow: this.autogrow,
      minHeight: this.fullHeight ? undefined : this.minHeight,
      height: this.fullHeight ? '100%' : undefined
    })

    return {
      ...editorConfig,
      setup: this.setup(),
    }
  }

  handleInPlaceActivate() {
    this.initEditor();
    this.active.set(true);
  }

  hanldeInPlaceDeactivate() {
    this.commitChange();
    this.active.set(false);
  }
  
  initEditor() {    
    // Disable Editors
    this.slideAuthorFacade.requestDisableEditors(this.id);
    
    // Disable Editor
    this.slideAuthorFacade.ofActionDispatchedDisableEditors.pipe(take(1)).subscribe((data: {action: FlatActions.SlideAuthorActions.DisableEditors}) => {
      if (data.action.id !== this.id) {
        this.active.set(false);
      }
    });
    
    // Setup Theme
    if (this.#editor) {
      this.#editor.options.set('skin_url', FlatModels.SlideAuthorModels.MedsurfThemeEditorStyleFile[FlatModels.SlideAuthorModels.MedsurfTheme.enum.dark]);
    }
  }

  public commitChange() {
    if (this.#editor && this.#editor.getContent() !== this.#previousValue) {
      const bookmark = this.#editor.selection.getBookmark(2);
      this.control?.setValue(this.#editor.getContent());
      this.valueChange.emit();
      this.#previousValue = this.#editor.getContent();
      this.#editor.selection.moveToBookmark(bookmark);
    }
  }

	protected setup() {
    return (editor: Editor) => {
      this.#editor = editor;
	    
	    editor.on('focusin', () => {
        this.#previousValue = editor.getContent();
	    })

      editor.on('blur', () => {
        this.commitChange();
      })

      editor.on('mouseleave', () => {
        this.commitChange();
      })
      
      editor.on('BeforeSetContent', (e) => {
        e.content = e.content.replace(
          /<img[^>]+src=["']data:[^"']+["'][^>]*>/gi,
          ''
        );
      });

      // Autofocus on init
      editor.on('init', function() {
        editor.execCommand('mceFocus');
      });
	    
      editor.ui.registry.addIcon('rectangle', '<i class="far fa-rectangle"></i>');
      editor.ui.registry.addIcon('rectangle-cross', '<i class="far fa-rectangle-xmark"></i>');
	    editor.ui.registry.addIcon('attention', '<i class="fa fa-exclamation-circle" style="color: #dc3545;"></i>');
	    editor.ui.registry.addIcon('note', '<i class="fa fa-check-circle" style="color: #28a745;"></i>');
	    editor.ui.registry.addIcon('info', '<i class="fa fa-info-circle" style="color: #4a90e2;"></i>');
      editor.ui.registry.addIcon('outline', '<i class="far fa-square-dashed"></i>');
      editor.ui.registry.addIcon('caret-marker', '<i class="fa fa-caret-right"></i>');
      editor.ui.registry.addIcon('plus-marker', '<i class="fa fa-plus"></i>');
      editor.ui.registry.addIcon('width', '<i class="fa fa-arrows-h"></i>');
	    editor.ui.registry.addIcon('objects-align-left', '<i class="fa fa-objects-align-left"></i>');
	    editor.ui.registry.addIcon('objects-align-center', '<i class="fa fa-objects-align-center-horizontal"></i>');
	    editor.ui.registry.addIcon('objects-align-right', '<i class="fa fa-objects-align-right"></i>');

      editor.ui.registry.addIcon('bold', '<i class="fa fa-bold"></i>');
      editor.ui.registry.addIcon('italic', '<i class="fa fa-italic"></i>');
      editor.ui.registry.addIcon('underline', '<i class="fa fa-underline"></i>')
      editor.ui.registry.addIcon('color-picker', '<i class="fa fa-palette"></i>');
      editor.ui.registry.addIcon('marker', '<i class="fa fa-map-marker"></i>');
      editor.ui.registry.addIcon('color-swatch-remove-color', '<i class="fa fa-ban"></i>');
      editor.ui.registry.addIcon('subscript', '<i class="fa fa-subscript"></i>');
      editor.ui.registry.addIcon('superscript', '<i class="fa fa-superscript"></i>');
      editor.ui.registry.addIcon('link', '<i class="fa fa-link"></i>');
      editor.ui.registry.addIcon('link-external', '<i class="fa fa-external-link"></i>');
      editor.ui.registry.addIcon('unlink', '<i class="fa fa-unlink"></i>');
      editor.ui.registry.addIcon('image', '<i class="fa fa-image"></i>');
      editor.ui.registry.addIcon('pdf', '<i class="fa fa-file-pdf"></i>');
      editor.ui.registry.addIcon('audio', '<i class="fa fa-volume"></i>');
      editor.ui.registry.addIcon('video', '<i class="fa fa-video"></i>');
      editor.ui.registry.addIcon('image-options', '<i class="fa fa-ellipsis"></i>');
      editor.ui.registry.addIcon('ordered-list', '<i class="fa fa-list-ol"></i>');
      editor.ui.registry.addIcon('unordered-list', '<i class="fa fa-list-ul"></i>');
      editor.ui.registry.addIcon('indent', '<i class="fa fa-indent"></i>');
      editor.ui.registry.addIcon('outdent', '<i class="fa fa-outdent"></i>');
      editor.ui.registry.addIcon('table', '<i class="fa fa-table"></i>');
      editor.ui.registry.addIcon('insert-character', '<i class="fa fa-omega"></i>');
      editor.ui.registry.addIcon('remove-formatting', '<i class="fa fa-remove-format"></i>');
      editor.ui.registry.addIcon('sourcecode', '<i class="fa fa-code"></i>');
      editor.ui.registry.addIcon('fullscreen', '<i class="fa fa-expand"></i>');
      editor.ui.registry.addIcon('help', '<i class="far fa-question-circle"></i>');
	    
			EditorPlugins.BoxPlugin.register(editor);
      EditorPlugins.AnnotationLinkPlugin.register(editor, this._ngZone, this.translate, this.dialogService);
      EditorPlugins.LinkPlugin.register(editor, this._ngZone, this.translate, this.dialogService);

      editor.ui.registry.addButton('unlink', {
        icon: 'unlink',
        tooltip: 'Remove link',
        onAction: () => editor.execCommand('Unlink')
      });
      
      EditorPlugins.ImageMediaPlugin.register(editor, this._ngZone, this.translate, this.dialogService, this.mediaControlService);
      EditorPlugins.VideoMediaPlugin.register(editor, this._ngZone, this.translate, this.dialogService, this.mediaControlService);
      EditorPlugins.PdfMediaPlugin.register(editor, this._ngZone, this.translate, this.dialogService, this.mediaControlService);
      EditorPlugins.AudioMediaPlugin.register(editor, this._ngZone, this.translate, this.dialogService, this.mediaControlService);
      
    };
  }
}
